import { PacerMessage } from './send-pacer.types';
export * from './send-pacer.types';

// The ONE status a message is in while still waiting to be sent. The pacer selects exactly
// these, and the server-side backpressure count (campaign-actions) counts exactly these — both
// reference this constant so they stay in lockstep. The UserMessage lifecycle is
// pending → sending → sent → confirmed → received (+ failed); only 'pending' is unsent-and-waiting.
// (A failed message re-enters the queue by being set back to 'pending' on retry.)
export const SENDABLE_STATUS = 'pending';

// Missing/invalid dueDate is treated as due-now (epoch 0) so such a message is never silently
// dropped — both the eligibility filter and the sort go through this, so the two agree.
const toMs = (d: string | Date): number => {
  const t = d instanceof Date ? d.getTime() : Date.parse(d);
  return Number.isNaN(t) ? 0 : t;
};

/**
 * Pure selection: the next message to dispatch, or null.
 * Eligible = sendable (status 'pending'), due (dueDate <= now), not in-flight.
 * Order = retryCount DESC (more-retried / near-expiry first), then dueDate ASC (oldest first).
 */
export function selectNextSendable(
  messages: PacerMessage[],
  inFlight: Set<string>,
  nowMs: number,
): PacerMessage | null {
  const eligible = messages.filter(
    (msg) =>
      msg &&
      msg._id &&
      !inFlight.has(msg._id) &&
      msg.status === SENDABLE_STATUS &&
      toMs(msg.dueDate) <= nowMs,
  );
  if (eligible.length === 0) return null;
  eligible.sort((a, b) => {
    const byRetry = (b.retryCount ?? 0) - (a.retryCount ?? 0);
    if (byRetry !== 0) return byRetry;
    return toMs(a.dueDate) - toMs(b.dueDate);
  });
  return eligible[0];
}
